import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminLayout from "../../commponents/layout/adminLayout";
import QuotationBasicInformation from "../../commponents/quotation/detail/QuotationBasicInformation";
import QuotationItemList from "../../commponents/quotation/detail/QuotationItemList";
import QuotationPriceInformation from "../../commponents/quotation/detail/QuotationPriceInformation";
import { getQuotationById } from "../../api/quotationApi";
import { showError } from "../../utils/ alert";
import { getAuthUser } from "../../api/authApi";
import { isForbiddenError } from "../../lib/axios";
import { isSalesEditLocked, isSalesRole } from "../../lib/roleAccess";

export interface QuotationDetailData {
  id: number;
  quotation_no: string;
  recipient_name: string;
  attention: string;
  email: string;
  sales: {
    id: number;
    name: string;
  };
  delivery_time: string;
  place_of_delivery: string;
  payment: string;
  validity: string;
  quotation_date: string;
  discount: string;
  ppn: string;
  subtotal: string;
  grand_total: string;
  status: {
    id: number;
    name: string;
    code: string;
    color: string;
  } | null;
  createdAt: string;
  items: {
    description: string;
    qty: number;
    unit: string;
    price: number;
    total: number;
  }[];
  customer: {
    id: number;
    customer_name: string;
    company_name: string;
    address: string;
    phone: string;
    email: string;
  };
}

export default function QuotationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [quotation, setQuotation] = useState<QuotationDetailData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        setLoading(true);
        const response = await getQuotationById(Number(id));

        setQuotation(response.data.data ?? response.data);
      } catch (error) {
        if (!isForbiddenError(error)) showError("Failed to load quotation detail.");
        navigate("/quotation");
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchDetail();
  }, [id]);

  if (loading || !quotation) {
    return (
      <AdminLayout>
        <div className="rounded-3xl bg-white p-6 text-center text-gray-400">Loading...</div>
      </AdminLayout>
    );
  }

  // Sales tidak bisa edit quotation yang sudah diproses (DO/PO/WO).
  const editDisabled =
    isSalesRole(getAuthUser()?.role) && isSalesEditLocked(quotation.status?.code);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="rounded-3xl bg-white p-6">
          <h2 className="mb-4 text-lg font-semibold">Detail Quotation</h2>

          <QuotationBasicInformation
            quotation={{
              recipient_name: quotation.customer.company_name,
              attention: quotation.customer.customer_name,
              email: quotation.customer.email,
              quotation_no: quotation.quotation_no,
              quotation_date: quotation.quotation_date,
              sales_name: quotation.sales.name,
              delivery_time: quotation.delivery_time,
              place_of_delivery: quotation.place_of_delivery,
              payment: quotation.payment,
              validity: quotation.validity,
              createdAt: quotation.createdAt,
            }}
            onEdit={() => navigate(`/quotation/edit/${quotation.id}`)}
            editDisabled={editDisabled}
          />
        </div>

        <div className="rounded-3xl bg-white p-6">
          <QuotationItemList items={quotation.items} />
        </div>

        <div className="rounded-3xl bg-white p-6">
          <QuotationPriceInformation
            subtotal={Number(quotation.subtotal)}
            discount={Number(quotation.discount)}
            ppn={Number(quotation.ppn)}
            grandTotal={Number(quotation.grand_total)}
          />
        </div>
      </div>
    </AdminLayout>
  );
}
